// frontend/src/pages/DashboardPage.tsx
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSalesData, APIResponseData, fetchGoalsData, fetchSupervisorGoalsData, fetchGerenteGoalsData, fetchMonthlyGoalsData, MonthlyGoal, GoalData } from './services/dashboardAPI';
import { useFilterStore, DatePreset } from '../../stores/filterStore';
import styles from './DashboardPage.module.css';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import DashboardSkeleton from './DashboardSkeleton';
import ProductBarChart from '../../features/products/components/BarChart/ProductBarChart';
import { fetchProductDetails, ProductAnalysisData } from '../../features/products/services/productsAPI';
import ProductDetailModal from '../../features/products/components/Product/ProductDetailModal';
import ClientDetailModal from './components/ClientDetailModal/ClientDetailModal';
import KPICard from '../../components/KPICard/KPICard';
import SalesLineChart from './components/SalesLineChart/SalesLineChart';
import ModernGoalWidget from './components/ModernGoalWidget/ModernGoalWidget';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';



import LineDonutChart from './components/DonutChart/LineDonutChart';
import TopClientsBarChart from './components/TopClientsBarChart/TopClientsBarChart';
import MonthlyBarChart from './components/MonthlyBarChart/MonthlyBarChart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import { DateNavigator, formatLocalDate } from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';
import { useThemeByEmpresa } from '../../hooks/useThemeByEmpresa';

interface AggregatedClientData {
    [key: string]: any;
    Nome_Cliente_Completo: string;
    ClientId: number;
    totalVenda: number;
    totalMargem: number;
    salesCount: number;
    estado: string;
    cidade: string;
    carteira: string;
}

interface AggregatedProductData {
    name: string;
    totalRevenue: number;
    totalVolume: number;
    totalMargem: number;
}


interface ProductRankingProps {
    byRevenue: AggregatedProductData[];
    byVolume: AggregatedProductData[];
    productImages?: Record<string, string>;
    productNames?: Record<string, string>;
    onProductClick?: (name: string) => void;
}

const ProductRanking = React.memo<ProductRankingProps>(({ byRevenue, byVolume, productImages, productNames, onProductClick }) => {
    return (
        <div className={styles.chartWidget_produtos}>
            <div className={styles.widgetHeader}>
                <h2 className={styles.widgetTitleInline}>Principais Modelos</h2>
            </div>
            {byRevenue.length === 0 ? (
                <p className={styles.noDataText}>Nenhum produto encontrado.</p>
            ) : (
                <div style={{ width: '100%', height: '320px' }}>
                    <ProductBarChart
                        byRevenue={byRevenue}
                        byVolume={byVolume}
                        activeTab="revenue"
                        productImages={productImages}
                        productNames={productNames}
                        onProductClick={onProductClick}
                    />
                </div>
            )}
        </div>
    );
});

const MemoizedSalesLineChart = React.memo(SalesLineChart);
const MemoizedTopClientsBarChart = React.memo(TopClientsBarChart);
const MemoizedLineDonutChart = React.memo(LineDonutChart);
const MemoizedMonthlyBarChart = React.memo(MonthlyBarChart);
const MemoizedModernGoalWidget = React.memo(ModernGoalWidget);

const TOP_COUNT = 10;
const ITEMS_PER_PAGE = 10;

const DashboardPage: React.FC = () => {
    const { initialDate, finalDate, activePreset, selectedCarteiraId, setPreset, setDates } = useFilterStore();
    const { theme: empresaTheme, empresa } = useThemeByEmpresa();
    const [selectedClient, setSelectedClient] = useState<{ name: string; id: number } | null>(null);
    const [visibleClients, setVisibleClients] = useState(ITEMS_PER_PAGE);
    const [productImages, setProductImages] = useState<Record<string, string>>({});
    const [productNames, setProductNames] = useState<Record<string, string>>({});
    const [selectedProductRef, setSelectedProductRef] = useState<string | null>(null);
    const [selectedClientModal, setSelectedClientModal] = useState<{ name: string; id?: number } | null>(null);

    const handleProductClick = useCallback((name: string) => {
        setSelectedProductRef(name);
    }, []);

    const handleClientClick = useCallback((clientName: string, clientId?: number) => {
        setSelectedClientModal({ name: clientName, id: clientId });
    }, []);

    const userBase = useAuthStore((state) => state.user?.base);
    const isSupervisor = useAuthStore((state) => state.role === 'supervisor' || state.role === 'diretor');
    const isGerente = useAuthStore((state) => state.role === 'gerente');
    const isTeamView = isSupervisor || isGerente;

    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const resSales = await fetchSalesData(initialDate!, finalDate!, selectedCarteiraId, activePreset || undefined, true);
            let resGoals;
            if (isGerente && !selectedCarteiraId) {
                const res = await fetchGerenteGoalsData(initialDate!, finalDate!);
                resGoals = res.data.goals;
            } else if (isSupervisor && !selectedCarteiraId) {
                const res = await fetchSupervisorGoalsData(initialDate!, finalDate!);
                resGoals = res.data.goals;
            } else {
                const targetBaseId = selectedCarteiraId || userBase?.toString();
                if (targetBaseId) {
                    const res = await fetchGoalsData(initialDate!, finalDate!, targetBaseId, true);
                    resGoals = res.data.goals;
                }
            }

            const salesQueryKey = ['salesData', initialDate, finalDate, selectedCarteiraId, activePreset, userBase];
            queryClient.setQueryData(salesQueryKey, resSales.data);
            
            if (resGoals) {
                const goalsQueryKey = ['goalsData', initialDate, finalDate, selectedCarteiraId, isTeamView, userBase];
                queryClient.setQueryData(goalsQueryKey, resGoals);
            }
        } catch (err) {
            console.error('Erro ao atualizar cache do dashboard:', err);
        } finally {
            setIsRefreshing(false);
        }
    };

    // ====== React Query: Sales Data ======
    const { data: salesResponse, isLoading: loading, error: salesError } = useQuery({
        queryKey: ['salesData', initialDate, finalDate, selectedCarteiraId, activePreset, userBase],
        queryFn: async () => {
            const response = await fetchSalesData(initialDate!, finalDate!, selectedCarteiraId, activePreset || undefined);
            return response.data as APIResponseData;
        },
        enabled: !!initialDate && !!finalDate,
        staleTime: 5 * 60 * 1000, // Caches for 5 minutes
        gcTime: 10 * 60 * 1000,
        refetchOnMount: false,
    });
    const apiData = salesResponse ?? null;
    const error = salesError ? 'Falha ao carregar dados do Dashboard.' : null;

    // ====== React Query: Goals Data ======
    const { data: goalsResponse } = useQuery({
        queryKey: ['goalsData', initialDate, finalDate, selectedCarteiraId, isTeamView, userBase],
        queryFn: async () => {
            if (isGerente && !selectedCarteiraId) {
                const res = await fetchGerenteGoalsData(initialDate!, finalDate!);
                return res.data.goals as GoalData[];
            } else if (isGerente && selectedCarteiraId) {
                const res = await fetchGoalsData(initialDate!, finalDate!, selectedCarteiraId);
                return res.data.goals as GoalData[];
            } else if (isSupervisor && !selectedCarteiraId) {
                const res = await fetchSupervisorGoalsData(initialDate!, finalDate!);
                return res.data.goals as GoalData[];
            } else if (isSupervisor && selectedCarteiraId) {
                const res = await fetchGoalsData(initialDate!, finalDate!, selectedCarteiraId);
                return res.data.goals as GoalData[];
            } else {
                const targetBaseId = userBase?.toString();
                if (!targetBaseId) return [] as GoalData[];
                const res = await fetchGoalsData(initialDate!, finalDate!, targetBaseId);
                return res.data.goals as GoalData[];
            }
        },
        enabled: !!initialDate && !!finalDate,
        staleTime: 5 * 60 * 1000, // Caches for 5 minutes
        gcTime: 10 * 60 * 1000,
        refetchOnMount: false,
    });
    const goalData = goalsResponse ?? null;

    // ====== Dados do ano para o gráfico mensal independente ======
    const currentYear = new Date().getFullYear();
    const { data: yearApiData } = useQuery({
        queryKey: ['yearlySales', currentYear, selectedCarteiraId, isTeamView, userBase],
        queryFn: async () => {
            const res = await fetchSalesData(
                `${currentYear}-01-01`,
                `${currentYear}-12-31`,
                selectedCarteiraId || undefined,
                undefined
            );
            return res.data as APIResponseData;
        },
        enabled: !!initialDate && !!finalDate,
        staleTime: 5 * 60 * 1000, // Caches for 5 minutes
        gcTime: 10 * 60 * 1000,
        refetchOnMount: false,
    });
    const yearTrendData = yearApiData?.trendChartData ?? null;
    const yearTrendKey = selectedCarteiraId || (isTeamView ? 'team' : userBase?.toString());
    const yearCurrentSeries = yearTrendData && yearTrendKey ? yearTrendData[yearTrendKey]?.current ?? [] : [];
    const yearPreviousSeries = yearTrendData && yearTrendKey ? yearTrendData[yearTrendKey]?.previous ?? [] : [];

    // ====== Metas mensais do ano para o gráfico Vendas por Mês ======
    const { data: monthlyGoalsData } = useQuery({
        queryKey: ['monthlyGoals', currentYear, selectedCarteiraId, userBase],
        queryFn: () => fetchMonthlyGoalsData(currentYear, selectedCarteiraId),
        enabled: !!initialDate && !!finalDate,
        staleTime: 5 * 60 * 1000, // Caches for 5 minutes
        gcTime: 10 * 60 * 1000,
        refetchOnMount: false,
    });
    const monthlyGoals = monthlyGoalsData?.monthlyGoals ?? [];



    const handleClientSelect = (clientName: string, clientId: number) => {
        setSelectedClient(prevClient =>
            (prevClient?.name === clientName ? null : { name: clientName, id: clientId }));
    };

    const handleLoadMoreClients = () => {
        setVisibleClients(prevVisible => prevVisible + ITEMS_PER_PAGE);
    };

    const { metrics, clientRanking, metricsChanges, topProductsByRevenue, topProductsByVolume, salesChartCurrentSeries, salesChartPreviousSeries, lineConcentrationData } = useMemo(() => {
        const rawData = apiData?.salesData || [];
        const trendChartData = apiData?.trendChartData || null;
        const previousPeriodMetrics = apiData?.previousPeriodMetrics || { totalVendas: 0, numeroVendas: 0, totalMargem: 0 };
        const previousLineRevenueData = apiData?.previousLineRevenue || [];

        const trendChartKey = selectedCarteiraId || (isTeamView ? 'team' : userBase?.toString());

        const salesChartCurrentSeries = trendChartData && trendChartKey ? trendChartData[trendChartKey]?.current : [];
        const salesChartPreviousSeries = trendChartData && trendChartKey ? trendChartData[trendChartKey]?.previous : [];

        let dataForMetrics = rawData;
        if (selectedClient) {
            dataForMetrics = dataForMetrics.filter(item => item.Nome_Cliente_Completo === selectedClient.name);
        }

        const rankingMap = new Map<string, AggregatedClientData>();
        rawData.forEach(item => {
            const clientName = item.Nome_Cliente_Completo;
            if (clientName) {
                if (!rankingMap.has(clientName)) {
                    rankingMap.set(clientName, {
                        ClientId: item.ClientId,
                        Nome_Cliente_Completo: clientName, totalVenda: 0, totalMargem: 0, salesCount: 0,
                        estado: item.ESTADO || '', cidade: item.CIDADE_CLIENTE || '', carteira: item.CARTEIRA || '',
                    });
                }
                const currentClient = rankingMap.get(clientName)!;
                currentClient.totalVenda += Number(item.Venda) || 0;
                currentClient.totalMargem += Number(item.Margem) || 0;
            }
        });
        const finalClientRanking = Array.from(rankingMap.values())
            .sort((a, b) => b.totalVenda - a.totalVenda);

        const productMap = new Map<string, AggregatedProductData>();
        rawData.forEach(item => {
            const productName = item.Item;
            if (productName) {
                if (!productMap.has(productName)) {
                    productMap.set(productName, { name: productName, totalRevenue: 0, totalVolume: 0, totalMargem: 0 });
                }
                const currentProduct = productMap.get(productName)!;
                currentProduct.totalRevenue += Number(item.Venda) || 0;
                currentProduct.totalVolume += Number(item.QUANT) || 0;
                currentProduct.totalMargem += Number(item.Margem) || 0;
            }
        });
        const aggregatedProducts = Array.from(productMap.values());
        const topProductsByRevenue = [...aggregatedProducts].sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, TOP_COUNT);
        const topProductsByVolume = [...aggregatedProducts].sort((a, b) => b.totalVolume - a.totalVolume).slice(0, TOP_COUNT);


        const currentLineMap = new Map<string, number>();
        rawData.forEach(item => {
            if (item.Linha) {
                const currentTotal = currentLineMap.get(item.Linha) || 0;
                currentLineMap.set(item.Linha, currentTotal + (Number(item.Venda) || 0));
            }
        });

        let totalVendas = 0;
        let totalMargem = 0;
        const clientesSet = new Set<string>();
        dataForMetrics.forEach(item => {
            totalVendas += Number(item.Venda) || 0;
            totalMargem += Number(item.Margem) || 0;
            if (item.Nome_Cliente_Completo) clientesSet.add(item.Nome_Cliente_Completo);
        });

        const calculatedMetrics = {
            totalVendas,
            totalMargem,
            numeroVendas: dataForMetrics.length,
            // clientesAtivos removido — substituído por Clientes Novos vindo da API
            mediaVenda: dataForMetrics.length > 0 ? totalVendas / dataForMetrics.length : 0,
            mediaMargem: totalVendas > 0 ? (totalMargem / totalVendas) * 100 : 0,
        };

        const prevMediaVenda = previousPeriodMetrics.numeroVendas > 0 ? previousPeriodMetrics.totalVendas / previousPeriodMetrics.numeroVendas : 0;
        const prevMediaMargem = previousPeriodMetrics.totalVendas > 0 ? (previousPeriodMetrics.totalMargem / previousPeriodMetrics.totalVendas) * 100 : 0;
        const calculatePercentageChange = (current: number, previous: number): number => {
            if (previous === 0) return current > 0 ? 100 : 0;
            return ((current - previous) / previous) * 100;
        };
        const metricsChanges = {
            totalVendas: calculatePercentageChange(calculatedMetrics.totalVendas, previousPeriodMetrics.totalVendas),
            mediaVenda: calculatePercentageChange(calculatedMetrics.mediaVenda, prevMediaVenda),
            mediaMargem: calculatedMetrics.mediaMargem - prevMediaMargem,
            numeroVendas: calculatePercentageChange(calculatedMetrics.numeroVendas, previousPeriodMetrics.numeroVendas),
        };

        return {
            metrics: calculatedMetrics,
            clientRanking: finalClientRanking,
            metricsChanges,
            topProductsByRevenue,
            topProductsByVolume,


            lineConcentrationData: Array.from(currentLineMap.entries())
                .map(([name, value]) => ({ name, value: Math.round(value) }))
                .filter(d => d.value > 0)
                .sort((a, b) => b.value - a.value),
            salesChartCurrentSeries,
            salesChartPreviousSeries,
            _lineTotalVendas: totalVendas,
        };
    }, [apiData, selectedClient, userBase, selectedCarteiraId, isTeamView]);

    // Busca imagens dos top modelos para exibir no tooltip
    const topProductRefs = useMemo(() => {
        const all = new Set<string>();
        topProductsByRevenue.forEach(p => all.add(p.name));
        return Array.from(all);
    }, [topProductsByRevenue]);

    useEffect(() => {
        let cancelled = false;
        const fetch = async () => {
            const results = await Promise.all(
                topProductRefs.map(ref =>
                    fetchProductDetails(ref)
                        .then(res => ({
                            ref,
                            img: (res.data as any).img as string | null,
                            descricao: (res.data as any).DESCRICAO as string | null,
                        }))
                        .catch(() => ({ ref, img: null, descricao: null }))
                )
            );
            if (cancelled) return;
            const imgMap: Record<string, string> = {};
            const nameMap: Record<string, string> = {};
            results.forEach(r => {
                if (r.img) imgMap[r.ref] = r.img;
                if (r.descricao) nameMap[r.ref] = r.descricao;
            });
            setProductImages(imgMap);
            setProductNames(nameMap);
        };
        if (topProductRefs.length > 0) fetch();
        return () => { cancelled = true; };
    }, [topProductRefs]);

    // Busca imagens/nomes dos produtos do cliente clicado que ainda faltam
    useEffect(() => {
        if (!selectedClientModal || !apiData?.salesData) return;
        let cancelled = false;
        // Descobre os produtos desse cliente que ainda nao tem imagem
        const clientRefs = new Set<string>();
        apiData.salesData
            .filter((s: any) => s.Nome_Cliente_Completo === selectedClientModal.name)
            .forEach((s: any) => { if (s.Item) clientRefs.add(s.Item); });
        const missing = Array.from(clientRefs).filter(ref => !productImages[ref]);
        if (!missing.length) return;
        const fetch = async () => {
            const results = await Promise.all(
                missing.map(ref =>
                    fetchProductDetails(ref)
                        .then(res => ({
                            ref,
                            img: (res.data as any).img as string | null,
                            descricao: (res.data as any).DESCRICAO as string | null,
                        }))
                        .catch(() => ({ ref, img: null, descricao: null }))
                )
            );
            if (cancelled) return;
            setProductImages(prev => {
                const next = { ...prev };
                results.forEach(r => { if (r.img) next[r.ref] = r.img; });
                return next;
            });
            setProductNames(prev => {
                const next = { ...prev };
                results.forEach(r => { if (r.descricao) next[r.ref] = r.descricao; });
                return next;
            });
        };
        fetch();
        return () => { cancelled = true; };
    }, [selectedClientModal, apiData]);

    // Produto selecionado para o modal de detalhes
    const selectedProduct = useMemo((): ProductAnalysisData | null => {
        if (!selectedProductRef) return null;
        const found = [...topProductsByRevenue, ...topProductsByVolume]
            .find(p => p.name === selectedProductRef);
        if (!found) return null;
        return {
            Produto: found.name,
            Linha: '',
            Descricao: found.name,
            QuantidadeTotal: found.totalVolume,
            FaturamentoTotal: found.totalRevenue,
            MargemTotal: found.totalMargem || 0,
            MargemPercentual: (found.totalMargem && found.totalRevenue) ? (found.totalMargem / found.totalRevenue) * 100 : 0,
            PrecoMedio: found.totalVolume > 0 ? found.totalRevenue / found.totalVolume : 0,
            NumClientes: 0,
        };
    }, [selectedProductRef, topProductsByRevenue, topProductsByVolume]);

    // Cliente selecionado no modal — agrega produtos dele a partir do rawData
    const selectedClientData = useMemo(() => {
        if (!selectedClientModal || !apiData?.salesData) return null;
        const clientSales = apiData.salesData.filter(
            (s: any) => s.Nome_Cliente_Completo === selectedClientModal.name
        );
        if (!clientSales.length) return null;

        const productMap = new Map<string, {
            name: string; ref: string; linha: string;
            totalRevenue: number; totalVolume: number; totalMargem: number;
        }>();
        let totalVenda = 0;
        let totalMargem = 0;

        clientSales.forEach((s: any) => {
            totalVenda += Number(s.Venda) || 0;
            totalMargem += Number(s.Margem) || 0;
            const ref = s.Item;
            if (!ref) return;
            if (!productMap.has(ref)) {
                productMap.set(ref, {
                    name: ref, ref,
                    linha: s.Linha || '',
                    totalRevenue: 0, totalVolume: 0, totalMargem: 0,
                });
            }
            const p = productMap.get(ref)!;
            p.totalRevenue += Number(s.Venda) || 0;
            p.totalVolume += Number(s.QUANT) || 0;
            p.totalMargem += Number(s.Margem) || 0;
        });

        const clientProducts = Array.from(productMap.values())
            .sort((a, b) => b.totalRevenue - a.totalRevenue);

        return { clientProducts, totalVenda, totalMargem };
    }, [selectedClientModal, apiData]);

    // Lógica de paginação para clientes
    const visibleClientsList = useMemo(() => {
        return clientRanking.slice(0, visibleClients);
    }, [clientRanking, visibleClients]);
    const hasMoreClients = visibleClients < clientRanking.length;

    const totalVendaRealizada = useMemo(() => {
        if (!apiData || !apiData.salesData || !Array.isArray(apiData.salesData)) return 0;
        return apiData.salesData.reduce((acc: number, curr: any) => {
            return acc + (Number(curr.Venda) || 0);
        }, 0);
    }, [apiData]);



    const totalMargemRealizada = useMemo(() => {
        if (!apiData || !apiData.salesData || !Array.isArray(apiData.salesData)) return 0;
        return apiData.salesData.reduce((acc: number, curr: any) => {
            return acc + (Number(curr.Margem) || 0);
        }, 0);
    }, [apiData]);

    const currentGoalData = Array.isArray(goalData) ? goalData[0] : goalData;
    const metaVenda = Number(currentGoalData?.goal) || 0;
    const metaMargem = Number(currentGoalData?.marginGoal) || 0;
    const metaScore = currentGoalData?.scoreFinal;

    // Próximo mês (para IMBISEG: vendas empurradas pra frente)
    const nextMonthTarget = useMemo(() => {
        if (!finalDate || monthlyGoals.length === 0) return undefined;
        const currentMonth = new Date(finalDate + 'T00:00:00').getMonth() + 1;
        const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
        const nextGoal = monthlyGoals.find(g => g.mes === nextMonth);
        return nextGoal?.meta;
    }, [finalDate, monthlyGoals]);

    const nextMonthSales = useMemo(() => {
        if (!finalDate || monthlyGoals.length === 0) return undefined;
        const currentMonth = new Date(finalDate + 'T00:00:00').getMonth() + 1;
        const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
        const nextGoal = monthlyGoals.find(g => g.mes === nextMonth);
        if (!nextGoal || !nextGoal.meta || !nextGoal.percVenda) return undefined;
        return nextGoal.meta * (nextGoal.percVenda / 100);
    }, [finalDate, monthlyGoals]);

    const nextMonthScore = useMemo(() => {
        if (!finalDate || monthlyGoals.length === 0) return undefined;
        const currentMonth = new Date(finalDate + 'T00:00:00').getMonth() + 1;
        const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
        const nextGoal = monthlyGoals.find(g => g.mes === nextMonth);
        if (!nextGoal || !nextGoal.percVenda) return undefined;
        // Para IMBISEG: score = percVenda (mesmo cálculo do gauge simplificado sem margem)
        return nextGoal.scoreFinal ?? nextGoal.percVenda;
    }, [finalDate, monthlyGoals]);

    const diasRestantesUteis = useMemo(() => {
        if (!finalDate) return undefined;
        const hoje = new Date();
        const fim = new Date(finalDate + 'T23:59:59');
        let count = 0;
        const current = new Date(hoje);
        current.setDate(current.getDate() + 1); // não conta hoje
        while (current <= fim) {
            const day = current.getDay();
            if (day !== 0 && day !== 6) count++;
            current.setDate(current.getDate() + 1);
        }
        return count;
    }, [finalDate]);

    // ── Periodo selecionado ──
    const [periodo, setPeriodo] = useState<'mensal' | 'anual' | 'semanal' | 'custom'>('mensal');
    const [detailType, setDetailType] = useState<'reativados' | 'novos' | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailPage, setDetailPage] = useState(0);
    const [customInicio, setCustomInicio] = useState('');
    const [customFim, setCustomFim] = useState('');
    const [dataRef, setDataRef] = useState(() => formatLocalDate(new Date()));

    // Fecha modal de detalhes ao pressionar Escape
    useEffect(() => {
        if (!detailOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setDetailOpen(false);
                setDetailType(null);
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [detailOpen]);

    // Calcula período fiscal (26 do mês anterior → 25 do mês de referencia)
    const applyFiscalMonth = (refDate: string) => {
        const base = new Date(refDate + 'T12:00:00');
        const year = base.getFullYear();
        const month = base.getMonth(); // 0-based
        setDates(
            new Date(year, month - 1, 26).toISOString().split('T')[0],
            new Date(year, month, 25).toISOString().split('T')[0]
        );
    };

    // Aplica período na filterStore
    const applyPreset = (p: 'mensal' | 'anual' | 'semanal') => {
        if (p === 'mensal') {
            if (isTeamView && !selectedCarteiraId) {
                // Supervisor/gerente sem carteira específica: calendário normal
                setPreset('currentMonth');
            } else {
                // Vendedor ou supervisor/gerente com carteira: fiscal 26-25
                const ref = dataRef || formatLocalDate(new Date());
                applyFiscalMonth(ref);
            }
        }
        else if (p === 'anual') setPreset('currentYear');
        else if (p === 'semanal') {
            // Semana atual: segunda a sexta
            const today = new Date();
            const day = today.getDay();
            const diff = today.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(today.getFullYear(), today.getMonth(), diff);
            const friday = new Date(monday);
            friday.setDate(monday.getDate() + 4);
            setDates(
                monday.toISOString().split('T')[0],
                friday.toISOString().split('T')[0]
            );
        }
    };

    // Aplica custom
    const applyCustom = () => {
        if (customInicio && customFim) setDates(customInicio, customFim);
    };

    // Formatar data pra exibir
    const formatDateLabel = (d: string) => {
        const date = new Date(d + 'T00:00:00');
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace(/\./g, '');
    };

    // Label amigável do período (ex: "Maio/25", "2025")
    const getPeriodLabel = (): string => {
        if (periodo === 'mensal' && finalDate) {
            const date = new Date(finalDate + 'T00:00:00');
            const month = date.toLocaleDateString('pt-BR', { month: 'long' });
            const year = date.getFullYear().toString().slice(-2);
            return month.charAt(0).toUpperCase() + month.slice(1) + '/' + year;
        }
        if (periodo === 'anual' && initialDate) {
            return new Date(initialDate + 'T00:00:00').getFullYear().toString();
        }
        // Semanal / custom — mostra range
        return `${formatDateLabel(initialDate)} — ${formatDateLabel(finalDate)}`;
    };

    const displayRange = getPeriodLabel();

    return (
        <div className={styles.pageContainer}>
            {/* ─── MODERN DASHBOARD HEADER (igual produtos/reclamacoes) ─── */}
            <ModernDashboardHeader
                onClientSelect={(client) => handleClientSelect(client.name, client.id)}
                selectedCarteira={selectedCarteiraId || ''}
                cacheInfo={{
                    cached: apiData?.cached,
                    cachedAt: apiData?.cachedAt,
                    onRefresh: handleRefresh,
                    isRefreshing: isRefreshing
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <CarteiraSelector />

                    {/* Botões de período (igual produtos/reclamacoes) */}
                    <div className={navStyles.periodContainer}>
                        {(['anual', 'mensal', 'semanal', 'custom'] as const).map((p) => (
                            <button
                                key={p}
                                onClick={() => {
                                    setPeriodo(p);
                                    if (p === 'mensal' || p === 'anual' || p === 'semanal') applyPreset(p);
                                }}
                                className={`${navStyles.periodButton} ${periodo === p ? navStyles.periodButtonActive : ''}`}
                            >
                                {p === 'custom' ? 'Personalizado' : p}
                            </button>
                        ))}
                    </div>

                    {/* DateNavigator com displayRange forçado (mostra as datas REAIS do store) */}
                    <div style={{ position: 'relative' }}>
                        <DateNavigator
                            label=""
                            periodo={periodo}
                            dataRef={dataRef}
                            dataInicio={customInicio}
                            dataFim={customFim}
                            displayRange={displayRange}
                            onChange={(vals: any) => {
                                if (vals.dataRef) {
                                    setDataRef(vals.dataRef);
                                    const base = new Date(vals.dataRef + 'T12:00:00');

                                    if (periodo === 'mensal') {
                                        if (isTeamView && !selectedCarteiraId) {
                                            // Supervisor/gerente sem carteira: mês calendário
                                            setDates(
                                                new Date(base.getFullYear(), base.getMonth(), 1).toISOString().split('T')[0],
                                                new Date(base.getFullYear(), base.getMonth() + 1, 0).toISOString().split('T')[0]
                                            );
                                        } else {
                                            applyFiscalMonth(vals.dataRef);
                                        }
                                    } else if (periodo === 'anual') {
                                        setDates(
                                            new Date(base.getFullYear(), 0, 1).toISOString().split('T')[0],
                                            new Date(base.getFullYear(), 11, 31).toISOString().split('T')[0]
                                        );
                                    } else if (periodo === 'semanal') {
                                        const day = base.getDay();
                                        const diff = base.getDate() - day + (day === 0 ? -6 : 1);
                                        const monday = new Date(base.getFullYear(), base.getMonth(), diff);
                                        const friday = new Date(monday);
                                        friday.setDate(monday.getDate() + 4);
                                        setDates(
                                            monday.toISOString().split('T')[0],
                                            friday.toISOString().split('T')[0]
                                        );
                                    }
                                }
                                if (vals.dataInicio && vals.dataFim) {
                                    setCustomInicio(vals.dataInicio);
                                    setCustomFim(vals.dataFim);
                                    setDates(vals.dataInicio, vals.dataFim);
                                    setPeriodo('custom');
                                }
                            }}
                        />
                    </div>

                </div>
            </ModernDashboardHeader>



            {selectedClient && (
                <div className={styles.activeFilterPill}>
                    Filtrando por: <strong>{selectedClient.name}</strong>
                    <Link to={`/clientes/${selectedClient.id}`} className={styles.detailsLink}>Ver Detalhes</Link>
                    <button onClick={() => setSelectedClient(null)} className={styles.clearFilterBtn}>×</button>
                </div>
            )}

            {loading ? (<DashboardSkeleton />)
                : error ? (<p className={styles.errorText}>{error}</p>)
                    : !apiData ? (<p className={styles.noDataText}>Nenhum dado encontrado para o período ou carteira selecionada.</p>)
                        : (
                            <>
                                <div className={styles.mainContentGrid}>
                                    {/* KPI Cards */}
                                    <div className={styles.kpiRowArea}>
                                        <KPICard
                                            title="Vendas"
                                            value={metrics.totalVendas.toLocaleString('pt-BR', { notation: 'compact', compactDisplay: 'short' })}
                                            subLabel1={`${metricsChanges.totalVendas >= 0 ? '+' : ''}${metricsChanges.totalVendas.toFixed(1)}%`}
                                        />
                                        <KPICard
                                            title="Ticket Médio"
                                            value={metrics.mediaVenda.toLocaleString('pt-BR', { notation: 'compact', compactDisplay: 'short' })}
                                            subLabel1={`${metricsChanges.mediaVenda >= 0 ? '+' : ''}${metricsChanges.mediaVenda.toFixed(1)}%`}
                                        />
                                        <KPICard
                                            title="Margem Média"
                                            value={`${metrics.mediaMargem.toFixed(1)}%`}
                                            subLabel1={`${metricsChanges.mediaMargem >= 0 ? '+' : ''}${metricsChanges.mediaMargem.toFixed(1)}%`}
                                        />
                                        <KPICard
                                            title="Clientes Novos"
                                            value={String(apiData?.newClientsCount ?? 0)}
                                            subLabel1={(apiData?.newClientsRevenue || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' })}
                                            accent
                                            onClick={() => { setDetailType('novos'); setDetailOpen(true); setDetailPage(0); }}
                                        />
                                        {apiData?.reactivatedClientsCount !== undefined ? (
                                            <KPICard
                                                title="Clientes Reativados"
                                                value={apiData.reactivatedClientsCount.toString()}
                                                subLabel1={(apiData.reactivatedClientsRevenue || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' })}
                                                accent
                                                onClick={() => { setDetailType('reativados'); setDetailOpen(true); setDetailPage(0); }}
                                            />
                                        ) : (
                                            <div style={{ visibility: 'hidden' }}><KPICard title="" value="" /></div>
                                        )}
                                    </div>

                                    {/* 1ª linha: Evolução │ Metas │ Modelos */}
                                    <div className={`${styles.chartWidget} ${styles.chartWidget_desempenho}`}>
                                        <h2 className={styles.widgetTitle}>Evolução de Vendas</h2>
                                        <MemoizedSalesLineChart
                                            currentSeries={salesChartCurrentSeries}
                                            previousSeries={salesChartPreviousSeries}
                                            targetGoal={periodo !== 'semanal' ? metaVenda : undefined}
                                        />
                                    </div>

                                    <div className={styles.chartWidget}>
                                        <MemoizedModernGoalWidget
                                            currentSales={totalVendaRealizada}
                                            targetSales={metaVenda}
                                            currentMargin={totalMargemRealizada}
                                            targetMargin={metaMargem}
                                            scoreFinal={metaScore}
                                            loading={loading}
                                            diasRestantes={diasRestantesUteis}
                                            nextMonthTarget={nextMonthTarget}
                                            nextMonthSales={nextMonthSales}
                                            nextMonthScore={nextMonthScore}
                                            empresa={empresa}
                                        />
                                    </div>

                                    <ProductRanking
                                        byRevenue={topProductsByRevenue}
                                        byVolume={topProductsByVolume}
                                        productImages={productImages}
                                        productNames={productNames}
                                        onProductClick={handleProductClick}
                                    />

                                    {/* 2ª linha: Inativos │ Clientes │ Participação */}
                                    <div className={styles.chartWidget} style={{ minHeight: 300 }}>
                                        <h2 className={styles.widgetTitle}>Maiores Clientes Inativos (90+ dias)</h2>
                                        {(!apiData?.inactiveClients || apiData.inactiveClients.length === 0) ? (
                                            <p className={styles.noDataText}>Nenhum cliente inativo no período</p>
                                        ) : (
                                            <div style={{ flex: 1, minHeight: 0 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={apiData.inactiveClients} layout="vertical" margin={{ top: 4, right: 60, left: 0, bottom: 4 }} barCategoryGap="25%">
                                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-primary)" />
                                                        <XAxis type="number" hide domain={[0, (max: number) => Math.max(max * 1.4, 30)]} />
                                                        <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} width={160} tick={({ x, y, payload }: any) => {
                                                            const name = String(payload.value ?? '');
                                                            const shortName = name.length > 18 ? name.substring(0, 16) + '...' : name;
                                                            return (
                                                                <text x={x - 8} y={y} textAnchor="end" fontSize={11} fontWeight={600} fill="var(--text-primary)">{shortName}</text>
                                                            );
                                                        }} />
                                                        <Tooltip content={({ active, payload }: any) => {
                                                            if (!active || !payload?.length) return null;
                                                            const d = payload[0].payload;
                                                            const daysSince = Math.floor((new Date().getTime() - new Date(d.ultimaCompra).getTime()) / (1000 * 60 * 60 * 24));
                                                            return (
                                                                <div style={{ background: 'var(--bg-widget)', borderRadius: 'var(--radius-base, 8px)', padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid var(--border-primary)', fontSize: 12 }}>
                                                                    <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', fontSize: 13, borderBottom: '1px solid var(--border-primary)', paddingBottom: 4 }}>{d.name}</p>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                                                        <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Última compra</span>
                                                                        <span style={{ fontWeight: 600, color: '#ef4444' }}>há {daysSince} {daysSince === 1 ? 'dia' : 'dias'}</span>
                                                                    </div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                                                        <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Total histórico</span>
                                                                        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{(d.totalHistorico || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' })}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                                                        <Bar dataKey="totalHistorico" fill="var(--theme-primary, #b91c1c)" radius={[0, 0, 0, 0]} barSize={16}>
                                                            <LabelList dataKey="totalHistorico" position="right" content={({ x, y, width, height, value }: any) => (
                                                                <text x={(x as number) + (width as number) + 6} y={(y as number) + (height as number) / 2} textAnchor="start" dominantBaseline="central" fontSize={11} fontWeight={700} fill="var(--text-primary)" style={{ pointerEvents: 'none' }}>
                                                                    {(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' })}
                                                                </text>
                                                            )} />
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.chartWidget} style={{ minHeight: 300 }}>
                                        <h2 className={styles.widgetTitle}>Principais Clientes</h2>
                                        <div style={{ flex: 1, minHeight: 0 }}>
                                            <MemoizedTopClientsBarChart
                                                data={visibleClientsList}
                                                totalVenda={metrics.totalVendas}
                                                onClientClick={handleClientClick}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.chartWidget} style={{ minHeight: 320 }}>
                                        <h2 className={styles.widgetTitle}>Participação por Linha</h2>
                                        <div style={{ flex: 1, minHeight: 0 }}>
                                            <MemoizedLineDonutChart
                                                data={lineConcentrationData}
                                                totalLabel="Faturamento"
                                            />
                                        </div>
                                    </div>
                                </div>

                                 {/* ─── Modal de detalhes dos clientes ─── */}
                                 {detailOpen && detailType && apiData && (
                                     <div style={{
                                         position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                                         display: 'flex', alignItems: 'center', justifyContent: 'center'
                                     }}>
                                         <div style={{
                                             background: 'var(--bg-widget)', borderRadius: 'var(--radius-base, 12px)', padding: 20, minWidth: 400, maxWidth: 560,
                                             maxHeight: '70vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid var(--border-primary)'
                                         }}>
                                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                 <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-primary)' }}>
                                                     {detailType === 'reativados' ? 'Clientes Reativados' : 'Novos Clientes'}
                                                 </h3>
                                                 <button onClick={() => { setDetailOpen(false); setDetailType(null); }}
                                                     style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-tertiary)' }}>×</button>
                                             </div>
                                             {(() => {
                                                 const lista = (detailType === 'reativados' ? apiData.reactivatedClientsList : apiData.newClientsList) || [];
                                                 const pageSize = 10;
                                                 const totalPages = Math.max(1, Math.ceil(lista.length / pageSize));
                                                 const safePage = Math.min(detailPage, totalPages - 1);
                                                 const paginated = lista.slice(safePage * pageSize, (safePage + 1) * pageSize);
                                                 return (
                                                     <>
                                                         {paginated.map((c: any, i: number) => (
                                                             <div key={i} style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid var(--border-primary)', fontSize: 12, gap: 12 }}>
                                                                 <div style={{ flex: 1, minWidth: 0 }}>
                                                                     <div style={{ fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nome}</div>
                                                                     <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, flexWrap: 'wrap' }}>
                                                                         {c.ultimaCompra && <span>{new Date(c.ultimaCompra).toLocaleDateString('pt-BR')}</span>}
                                                                         {c.doc && <span>{c.doc}</span>}
                                                                     </div>
                                                                 </div>
                                                                 <span style={{ fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', alignSelf: 'center' }}>{(c.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' })}</span>
                                                             </div>
                                                         ))}
                                                         {!lista.length && <p style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>Nenhum cliente listado</p>}
                                                         {totalPages > 1 && (
                                                             <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 12 }}>
                                                                 <button disabled={safePage === 0} onClick={() => setDetailPage(safePage - 1)}
                                                                     style={{ padding: '4px 10px', borderRadius: 'var(--radius-base, 6px)', border: '1px solid var(--border-primary)', background: 'var(--bg-widget)', fontSize: 11, fontWeight: 700, cursor: 'pointer', color: safePage === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>‹</button>
                                                                 <span style={{ padding: '4px 8px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', alignSelf: 'center' }}>{safePage + 1} / {totalPages}</span>
                                                                 <button disabled={safePage >= totalPages - 1} onClick={() => setDetailPage(safePage + 1)}
                                                                     style={{ padding: '4px 10px', borderRadius: 'var(--radius-base, 6px)', border: '1px solid var(--border-primary)', background: 'var(--bg-widget)', fontSize: 11, fontWeight: 700, cursor: 'pointer', color: safePage >= totalPages - 1 ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>›</button>
                                                             </div>
                                                         )}
                                                     </>
                                                 );
                                             })()}
                                         </div>
                                     </div>
                                 )}

                                {selectedProduct && initialDate && finalDate && (
                                    <ProductDetailModal
                                        product={selectedProduct}
                                        startDate={initialDate}
                                        endDate={finalDate}
                                        onClose={() => setSelectedProductRef(null)}
                                    />
                                )}

                                {selectedClientData && selectedClientModal && (
                                    <ClientDetailModal
                                        clientName={selectedClientModal.name}
                                        clientProducts={selectedClientData.clientProducts}
                                        productImages={productImages}
                                        productNames={productNames}
                                        totalVenda={selectedClientData.totalVenda}
                                        totalMargem={selectedClientData.totalMargem}
                                        onClose={() => setSelectedClientModal(null)}
                                    />
                                )}

                                {/* Gráfico mensal independente — abaixo do grid */}
                                <div className={styles.chartWidget} style={{ width: '100%', minHeight: 300, marginTop: 8 }}>
                                    <h2 className={styles.widgetTitle} style={{ textAlign: 'center' }}>Vendas por Mês</h2>
                                    <div style={{ flex: 1, minHeight: 0 }}>
                                        <MemoizedMonthlyBarChart
                                            currentSeries={yearCurrentSeries}
                                            previousSeries={yearPreviousSeries}
                                            monthlyGoals={monthlyGoals}
                                            showComparison={false}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
        </div>
    );
};

export default DashboardPage;